import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardTitle, Btn, Insight, MetricCard, Grid } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const STATUS_CLR = { running: '#28a745', paused: '#fd7e14', completed: '#6F8FA7' };
const BLANK = { name: '', hypothesis: '', variantA: '', variantB: '', metric: '' };

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

export default function Abtest() {
  const [tests, setTests]     = useState([]);
  const [form, setForm]       = useState(BLANK);
  const [adding, setAdding]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [toast, setToast]     = useState(null);
  const [me, setMe]           = useState('admin');
  const isMobile              = useIsMobile();

  function showToast(msg, type) {
    setToast({ msg, type: type || 'ok' });
    setTimeout(() => setToast(null), 3000);
  }

  const loadTests = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_get_ab_tests');
      if (error === null || error === undefined) setTests(data || []);
    } catch (_e) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadTests();
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) setMe(data.user.email?.split('@')[0] || 'admin');
    });
    const channel = supabase
      .channel('ab_tests_live')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'ab_tests' }, () => loadTests())
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [loadTests]);

  async function handleSave() {
    if (!form.name.trim() || !form.hypothesis.trim()) return;
    setSaving(true);
    try {
      await supabase.rpc('admin_create_ab_test', {
        p_name: form.name.trim(), p_hypothesis: form.hypothesis.trim(),
        p_variant_a: form.variantA, p_variant_b: form.variantB,
        p_metric: form.metric, p_created_by: me,
      });
      setForm(BLANK); setAdding(false);
      showToast('Experiment saved to Supabase');
      await loadTests();
    } catch (_e) { showToast('Failed to save', 'warn'); }
    setSaving(false);
  }

  async function setStatus(id, status) {
    try {
      await supabase.rpc('admin_update_ab_test', { p_id: id, p_status: status });
      setTests(l => l.map(t => t.id === id ? { ...t, status } : t));
    } catch (_e) { showToast('Failed to update', 'warn'); }
  }

  async function handleDelete(id) {
    try {
      await supabase.rpc('admin_delete_ab_test', { p_id: id });
      setTests(l => l.filter(t => t.id !== id));
      showToast('Experiment removed', 'warn');
    } catch (_e) { showToast('Failed to delete', 'warn'); }
  }

  async function markComplete(id) {
    const outcome = window.prompt('What was the outcome or winning variant?');
    if (!outcome) return;
    try {
      await supabase.rpc('admin_update_ab_test', { p_id: id, p_status: 'completed', p_outcome: outcome });
      setTests(l => l.map(t => t.id === id ? { ...t, status: 'completed', outcome } : t));
      showToast('Experiment marked complete');
    } catch (_e) { showToast('Failed to update', 'warn'); }
  }

  const running   = tests.filter(t => t.status === 'running').length;
  const completed = tests.filter(t => t.status === 'completed').length;

  const field = (label, key, placeholder) => (
    <div>
      <div style={{ fontSize: '.63rem', color: '#6F8FA7', marginBottom: 3 }}>{label}</div>
      <input
        value={form[key]}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        placeholder={placeholder}
        style={{ fontSize: '.72rem', border: '1px solid #d8eaf4', borderRadius: 6, padding: '5px 8px', color: '#1a3040', width: '100%', fontFamily: F, outline: 'none' }}
        onFocus={e => e.target.style.outline = '2px solid #0066cc'}
        onBlur={e => e.target.style.outline = 'none'}
      />
    </div>
  );

  return (
    <div style={{ fontFamily: F }}>
      {toast && (
        <div style={{ position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', background: toast.type === 'warn' ? '#7c3a00' : '#1b5e20', color: '#fff', padding: '10px 20px', borderRadius: 10, fontSize: '.78rem', fontWeight: 500, zIndex: 9999, boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
      )}

      <Grid cols={3}>
        <MetricCard label="Running"      value={loading ? '...' : running}       sub="active experiments"  trend={running > 0 ? 'up' : null} />
        <MetricCard label="Completed"    value={loading ? '...' : completed}     sub="finished experiments" />
        <MetricCard label="Total logged" value={loading ? '...' : tests.length}  sub="all time - Supabase" />
      </Grid>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: adding ? 14 : 0 }}>
          <CardTitle>Experiment log</CardTitle>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: '.58rem', color: '#2da06a', display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#2da06a', display: 'inline-block' }} />
              Live
            </span>
            <Btn variant={adding ? 'outline' : 'primary'} onClick={() => setAdding(a => !a)}>{adding ? 'Cancel' : '+ New experiment'}</Btn>
          </div>
        </div>

        {adding && (
          <div style={{ background: '#f5fafc', border: '1px solid #d8eaf4', borderRadius: 9, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 8 }}>
              {field('Experiment name *', 'name', 'e.g. Paywall headline copy')}
              {field('Primary metric', 'metric', 'e.g. Paywall conversion rate')}
            </div>
            <div style={{ marginBottom: 8 }}>
              {field('Hypothesis *', 'hypothesis', 'We believe that... because...')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 12 }}>
              {field('Variant A - control', 'variantA', 'Current version')}
              {field('Variant B - test', 'variantB', 'New version')}
            </div>
            <Btn onClick={handleSave} disabled={saving || !form.name.trim() || !form.hypothesis.trim()}>
              {saving ? 'Saving...' : 'Save experiment'}
            </Btn>
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9eb8c8', fontSize: '.75rem', padding: '8px 0' }}>Loading...</div>
        ) : tests.length === 0 ? (
          <Insight type="info">No experiments logged yet. Track copy tests, pricing variants, UI changes. All data syncs to Supabase in real time across all admin sessions.</Insight>
        ) : (
          tests.map(t => (
            <div key={t.id} style={{ border: '1px solid #edf6fb', borderRadius: 9, padding: '12px 14px', marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '.78rem', fontWeight: 600, color: '#1a3040', marginBottom: 2 }}>{t.name}</div>
                  <div style={{ fontSize: '.66rem', color: '#6F8FA7' }}>{t.hypothesis}</div>
                </div>
                <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexShrink: 0, marginLeft: 10 }}>
                  <span style={{ fontSize: '.62rem', fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: (STATUS_CLR[t.status] || '#999') + '22', color: STATUS_CLR[t.status] || '#999' }}>{t.status}</span>
                  <button onClick={() => handleDelete(t.id)} style={{ padding: '2px 7px', background: '#fef0ef', color: '#8b1c1c', border: 'none', borderRadius: 5, fontSize: '.65rem', cursor: 'pointer', fontFamily: F }}>x</button>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <div style={{ background: '#f5fafc', borderRadius: 6, padding: '7px 9px' }}>
                  <div style={{ fontSize: '.57rem', color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>A - Control</div>
                  <div style={{ fontSize: '.7rem', color: '#314F5C' }}>{t.variant_a || '-'}</div>
                </div>
                <div style={{ background: '#f0fbf5', borderRadius: 6, padding: '7px 9px' }}>
                  <div style={{ fontSize: '.57rem', color: '#9eb8c8', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 2 }}>B - Test</div>
                  <div style={{ fontSize: '.7rem', color: '#314F5C' }}>{t.variant_b || '-'}</div>
                </div>
              </div>
              {t.metric && <div style={{ fontSize: '.64rem', color: '#9eb8c8', marginBottom: 6 }}>Metric: {t.metric}</div>}
              <div style={{ fontSize: '.6rem', color: '#b0c8d4', marginBottom: 8 }}>
                {t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}{t.created_by ? ' by ' + t.created_by : ''}
              </div>
              {t.status !== 'completed' ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  {t.status === 'running'  && <Btn small variant="outline" onClick={() => setStatus(t.id, 'paused')}>Pause</Btn>}
                  {t.status === 'paused'   && <Btn small variant="outline" onClick={() => setStatus(t.id, 'running')}>Resume</Btn>}
                  <Btn small variant="green" onClick={() => markComplete(t.id)}>Mark complete</Btn>
                </div>
              ) : (
                <div style={{ fontSize: '.68rem', color: '#2da06a', background: '#f0fbf5', borderRadius: 6, padding: '5px 9px' }}>Outcome: {t.outcome}</div>
              )}
            </div>
          ))
        )}
      </Card>
    </div>
  );
}
